import { useState, useEffect } from 'react';
import { Container, Spinner, Alert, Card } from "react-bootstrap";
import Course  from "./Course";

import instance from "../../../services/api";

const STUDENT_YEAR_LEVEL_GROUPS = ["5-6", "7-8", "9-10"];

const isStudentCourse = (course) => {
    if (Array.isArray(course.audience)) {
        return course.audience.includes("Student");
    }

    return course.audience === "Student";
};

const isOrganisationCourse = (course) => {
    if (!course.audience) {
        return true;
    }

    if (Array.isArray(course.audience)) {
        return course.audience.length === 0 || course.audience.includes("School");
    }

    return course.audience === "School";
};

const groupStudentCoursesByYearLevel = (courses) => {
    return courses.reduce((acc, course) => {
        const group = course.studentYearLevelGroup || "Ungrouped";

        if (!acc[group]) {
            acc[group] = [];
        }

        acc[group].push(course);
        return acc;
    }, {
        "5-6": [],
        "7-8": [],
        "9-10": [],
        Ungrouped: [],
    });
};

const CoursesScreen = ({ isOrg, isAdmin, userGroup }) => {
    const [organisationCourses, setOrganisationCourses] = useState([]);
    const [studentCoursesByGroup, setStudentCoursesByGroup] = useState(groupStudentCoursesByYearLevel([]));
    const [subscribedYearLevelGroup, setSubscribedYearLevelGroup] = useState("");
    const [subscriptionExpiryDate, setSubscriptionExpiryDate] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);

        async function fetchData() {
            try {
                if (!isOrg && !isAdmin && userGroup === "Student") {
                    const [verifyResponse, courseResponse] = await Promise.all([
                        instance.get("/auth/verify"),
                        instance.get("/course"),
                    ]);

                    const studentYearLevelGroup = verifyResponse?.data?.studentCourseYearLevelGroup || "";
                    const expiryRaw = verifyResponse?.data?.studentCourseAccessExpiryDate || null;
                    const expiryDate = expiryRaw ? new Date(expiryRaw) : null;

                    setSubscribedYearLevelGroup(studentYearLevelGroup);
                    setSubscriptionExpiryDate(expiryDate);
                    setStudentCoursesByGroup(groupStudentCoursesByYearLevel(Array.isArray(courseResponse.data) ? courseResponse.data : []));
                    return;
                }

                const courseResponse = await instance.get("/course");
                const allCourses = Array.isArray(courseResponse.data) ? courseResponse.data : [];
                const organisationVisibleCourses = allCourses.filter(isOrganisationCourse);
                const studentVisibleCourses = allCourses.filter(isStudentCourse);

                setOrganisationCourses(organisationVisibleCourses);
                setStudentCoursesByGroup(groupStudentCoursesByYearLevel(studentVisibleCourses));
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [isAdmin, isOrg, userGroup]);

    const hasActiveStudentSubscription = Boolean(
        subscribedYearLevelGroup &&
        subscriptionExpiryDate &&
        !Number.isNaN(subscriptionExpiryDate.getTime()) &&
        subscriptionExpiryDate > new Date()
    );

    const renderCourseList = (courses, emptyMessage) => (
        courses.length > 0 ? (
            courses.map((course, index) => (
                <Course key={`${course._id || course.title}-${index}`} {...course} />
            ))
        ) : (
            <p>{emptyMessage}</p>
        )
    );

    const renderAdminStudentCourses = () => {
        const allGroups = [...STUDENT_YEAR_LEVEL_GROUPS];
        if (studentCoursesByGroup.Ungrouped.length > 0) {
            allGroups.push("Ungrouped");
        }

        return allGroups.map((group) => (
            <Card className="page-section" key={group}>
                <Card.Body>
                    <h4 className="fw-bold">Year Levels {group}</h4>
                    <hr />
                    {renderCourseList(
                        studentCoursesByGroup[group] || [],
                        `No student courses are available for year levels ${group} yet.`
                    )}
                </Card.Body>
            </Card>
        ));
    };

    return (
        <Container className="page-shell">
            <section className="page-header">
                <h2 className="page-title">
                    {isAdmin ? "Courses" : isOrg ? "Professional Development Courses" : "My Online Courses"}
                </h2>
                <p className="page-subtitle">
                    {isAdmin
                        ? "Review professional development courses and student course libraries from one place."
                        : isOrg
                            ? "Browse all available school and organisation professional learning modules."
                            : "Access your subscribed year-level course content from this dashboard."}
                </p>
            </section>
            { loading ? (
                <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                </Spinner>
            ) : (
                <>
                    {isAdmin ? (
                        <>
                            <div className="page-section">
                                <h3 className="mb-3">Organisation Courses</h3>
                                {renderCourseList(
                                    organisationCourses,
                                    "No organisation courses are available right now."
                                )}
                            </div>
                            <section className="page-header pt-0">
                                <h3 className="page-title">Student Courses</h3>
                                <p className="page-subtitle">All student course libraries grouped by year levels.</p>
                            </section>
                            {renderAdminStudentCourses()}
                        </>
                    ) : isOrg ? (
                        renderCourseList(
                            organisationCourses,
                            "No organisation courses are available right now."
                        )
                    ) : hasActiveStudentSubscription ? (
                        <Card className="page-section">
                            <Card.Body>
                                <h4 className="fw-bold">Year Levels {subscribedYearLevelGroup}</h4>
                                <hr />
                                {renderCourseList(
                                    studentCoursesByGroup[subscribedYearLevelGroup] || [],
                                    "No courses are available for your subscribed year level group yet."
                                )}
                            </Card.Body>
                        </Card>
                    ) : (
                        <Alert variant="info">
                            You do not currently have an active student course subscription.
                        </Alert>
                    )}
                </>
            )}
        </Container>
    );
};

export default CoursesScreen;